import React, { useState, useEffect, useId } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { faQuestionCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { selectUser } from '../../redux/userSlice'
import { selectModules } from '../../redux/moduleSlice'
import { hasUserPermission } from '../../utils/permissionHelper'
import LayoutSidebar from '../../components/LayoutSidebar'
import TableHeader from '../../components/TableHeader'
import TableRow from '../../components/TableRow'
import { fetchUserNotification, selectUserNotification } from "../../redux/userNotificationSlice"
import Loading from "../../components/Loading"
import Pagination from "../../components/Pagination"
import { selectAuth } from "../../redux/authSlice"

/**
 * Main list page for all of LOX Admin. This page is controlled by the BE module system and renders
 *  the table and the data for the table dynamically based on that system.
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
export default function UserNotifications() {
  const navi = useNavigate()
  const startId = useId()

  // Redux Selectors
  const dispatch = useDispatch()
  const auth = useSelector(selectAuth)
  const modules = useSelector(selectModules)
  const active = useSelector(selectUser)
  const userNotification = useSelector(selectUserNotification)

  // Local State
  const [module, setModule] = useState()
  const [notificationCount, setNotificationCount] = useState(-1)
  const [columns, setColumns] = useState([])
  const [sortableFields, setSortableFields] = useState([])
  const [loaded, setLoaded] = useState(false)
  const [partialLoad, setPartialLoad] = useState(false)

  /**
   * Check if the user has correct permissions
   */
  useEffect(() => {
    if (active.status === 'succeeded' && module) {
      const activePermissions = active.spoofed_user.user_permission_options ? active.spoofed_user.user_permission_options : active.data.user_permission_options
      if (!hasUserPermission(activePermissions, 'notification_read')) {
        navi('/403')
      }
    }
  }, [active, module])

  /**
   * Ensure that the module being accessed actually exists
   */
  useEffect(() => {
    if (modules.data.length > 0) {
      const m = modules.data.find((e) => e.code_key === 'notification')
      if (m) {
        setModule(m)
      } else {
        navi('/404')
      }
    }
  }, [modules.data])

  useEffect(() => {
    if (userNotification.status === 'succeeded') {
      setNotificationCount(userNotification.data.length)
      setLoaded(true)
      setPartialLoad(true)
    }
  }, [userNotification])

  /**
   * Filter module column data into state where needed
   */
  useEffect(() => {
    if (module) {
      const fields = module.module_fields.filter(
        (column) => column.is_show_in_list_view
      )
      const sortedByRank = fields.sort((a, b) => {
        return a.list_display_rank - b.list_display_rank;
      });

      setColumns(sortedByRank)
    }
  }, [module])

  const paginate = async (url) => {
    setPartialLoad(false)
    try {
      await dispatch(
        fetchUserNotification({
          accessToken: auth.accessToken,
          endpoint: url.replace(`${process.env.REACT_APP_API_DOMAIN}/`, ''),
        })
      )
      setPartialLoad(true)
    } catch (error) {
      setPartialLoad(true)
    }
  }

  if (module) {
    const count = 0

    // @TODO: Potentially need to add back in pagination
    return (
      <LayoutSidebar
        contentTitle={notificationCount !== -1 ? (
          <>
            <span>Notification List&nbsp;</span>
            <span>
              ({userNotification?.meta?.total})
            </span>
            {module.helper_text && (
              <span className="ml-1 lox-no-border lox-tooltip-moveable has-tooltip-arrow has-tooltip-multiline has-tooltip-right has-tooltip-primary" data-tooltip={module.helper_text}>
                <FontAwesomeIcon icon={faQuestionCircle} className="is-size-4 has-text-link" />
              </span>
            )}
          </>
        ) : (
          <>
            <span>Notification List&nbsp;</span>
            <Loading size="is-size-4" />
          </>
        )}
        content={loaded || partialLoad ? (
          <div className="has-pagination pt-3 mb-5">
            <div className={`${userNotification.data?.length <= 3 && 'lox-hooktable-dataless'} lox-hooktable table-container mb-0 position-relative`}>
              {!partialLoad && loaded && (
                <div className="loader-wrapper">
                  <Loading />
                </div>
              )}
              <div className="list-table-container">
                <table className="lox-hooktable-table table is-striped is-narrow is-hoverable is-fullwidth is-info has-sticky-column has-view-button">
                  <TableHeader
                    columns={columns}
                    startId={startId}
                    count={count}
                    hasActions={false}
                    hasView
                    setPartialLoad={setPartialLoad}
                    sortableFields={sortableFields}
                    url={module.code_key}
                  />
                  {userNotification.data.length === 0 && (
                    <tbody>
                      <tr>
                        <td className="is-size-6 is-italic" style={{ textAlign: "center", padding: "20px", color: "grey" }} colSpan={columns.length + 1}>No Record(s) Found</td>
                      </tr>
                    </tbody>
                  )}
                  <tbody className={partialLoad && !loaded ? 'loading' : ''}>
                    <TableRow rows={userNotification.data} columns={columns} hasActions={false} hasView baseName="notification" setPartialLoad={setPartialLoad} />
                  </tbody>
                </table>
              </div>
            </div>
            {/* If there is more than one page, show pagination */}
            {userNotification.meta && Object.keys(userNotification.meta).length > 0 && userNotification.meta.last_page !== 1 && (
              <Pagination meta={userNotification.meta} setPartialLoad={setPartialLoad} paginateCallback={paginate} />
            )}
          </div>
        ) : <Loading />}
      />
    )
  }
}
